import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../client.ts';
import type { VisitorGroup } from '../schemas/visitorGroup.ts';

/**
 * Visitor-group hooks. List is role-scoped just like /api/visitors —
 * the backend filters by `entries_id = role`. Membership management
 * (add/remove visitor) happens via Visitor.inGroup, not on the group
 * payload itself.
 */

export function useVisitorGroups(roleId: number | null | undefined) {
  return useQuery({
    queryKey: ['visitor-groups', 'list', roleId],
    queryFn: async (): Promise<VisitorGroup[]> => {
      const res = await api.get<VisitorGroup[]>('/api/visitor-groups', {
        params: { role: roleId ?? -1 },
      });
      if (res.status !== 200) throw new Error(`/api/visitor-groups → ${res.status}`);
      return res.data;
    },
    enabled: roleId != null && roleId !== -1,
  });
}

export function useVisitorGroup(id: number | null | undefined) {
  return useQuery({
    queryKey: ['visitor-group', id],
    queryFn: () => http.get<VisitorGroup>(`/api/visitor-groups/${id}`),
    enabled: id != null,
  });
}

export function useSaveVisitorGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (g: VisitorGroup) => {
      if (g.id == null) return http.post<VisitorGroup>('/api/visitor-groups', g);
      return http.put<VisitorGroup>('/api/visitor-groups', g);
    },
    onSuccess: saved => {
      qc.invalidateQueries({ queryKey: ['visitor-groups'] });
      if (saved.id != null) qc.invalidateQueries({ queryKey: ['visitor-group', saved.id] });
    },
  });
}

export function useDeleteVisitorGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete(`/api/visitor-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visitor-groups'] }),
  });
}
